import useSWR from 'swr'
import { api } from '../services/api'
import { useSelectedAccountId } from './useSubscription'

/**
 * Fair-use limits for E-Commerce
 */
export const ECOMMERCE_VIDEO_FAIR_USE_LIMIT = 1000
export const WARNING_THRESHOLD_PERCENT = 80

/**
 * Default quota values (defined outside hook to avoid recreation)
 */
const DEFAULT_QUOTA = {
  used: 0,
  limit: 0,
  remaining: 0,
  unlimited: false,
}

/**
 * Calculate percentage from quota values
 */
function calculatePercentage(quota) {
  if (quota.unlimited) return 0
  if (quota.limit <= 0) return 0
  return Math.round((quota.used / quota.limit) * 100)
}

/**
 * SWR fetcher for ecommerce usage data
 */
async function fetcher() {
  return api.getEcommerceUsage()
}

/**
 * Hook for accessing E-Commerce usage/quota data
 *
 * Returns usage information for:
 * - videos: Scheduled ecommerce videos (fair-use limit: 1000/month)
 * - products: Imported products (plan-based: 50/250/unlimited)
 * - feeds: Connected feeds (plan-based: 1/3/unlimited)
 *
 * @returns {Object} Usage data and SWR state
 */
export function useEcommerceUsage() {
  const accountId = useSelectedAccountId()

  // Only fetch if we have an account
  const swrKey = accountId ? ['ecommerce-usage', accountId] : null

  const { data, error, mutate } = useSWR(swrKey, fetcher, {
    revalidateOnFocus: false,
    revalidateOnReconnect: true,
    dedupingInterval: 30000,
    refreshInterval: 60000,
  })

  const videos = data?.videos || { ...DEFAULT_QUOTA, limit: ECOMMERCE_VIDEO_FAIR_USE_LIMIT }
  const products = data?.products || DEFAULT_QUOTA
  const feeds = data?.feeds || DEFAULT_QUOTA

  const videosPercentage = calculatePercentage(videos)
  const productsPercentage = calculatePercentage(products)
  const feedsPercentage = calculatePercentage(feeds)

  return {
    videos: {
      ...videos,
      percentage: videosPercentage,
      isNearLimit: !videos.unlimited && videos.limit > 0 && videosPercentage >= WARNING_THRESHOLD_PERCENT,
      // Only exceeded if limit > 0 (avoid false positive when limit is 0/unknown)
      isExceeded: !videos.unlimited && videos.limit > 0 && videos.used >= videos.limit,
      resetDate: data?.videos?.reset_date || null,
    },
    products: {
      ...products,
      percentage: productsPercentage,
      isNearLimit: !products.unlimited && products.limit > 0 && productsPercentage >= WARNING_THRESHOLD_PERCENT,
      // Only exceeded if limit > 0 (avoid false positive when limit is 0/unknown)
      isExceeded: !products.unlimited && products.limit > 0 && products.used >= products.limit,
    },
    feeds: {
      ...feeds,
      percentage: feedsPercentage,
      isNearLimit: !feeds.unlimited && feeds.limit > 0 && feedsPercentage >= WARNING_THRESHOLD_PERCENT,
      // Only exceeded if limit > 0 (avoid false positive when limit is 0/unknown)
      isExceeded: !feeds.unlimited && feeds.limit > 0 && feeds.used >= feeds.limit,
    },
    // Convenience flags
    hasAnyWarning: videosPercentage >= WARNING_THRESHOLD_PERCENT ||
                   productsPercentage >= WARNING_THRESHOLD_PERCENT ||
                   feedsPercentage >= WARNING_THRESHOLD_PERCENT,
    hasAnyExceeded: (videos.limit > 0 && videos.used >= videos.limit && !videos.unlimited) ||
                    (products.limit > 0 && products.used >= products.limit && !products.unlimited) ||
                    (feeds.limit > 0 && feeds.used >= feeds.limit && !feeds.unlimited),
    loading: !error && !data && accountId !== null,
    error,
    mutate,
  }
}

/**
 * Invalidate ecommerce usage cache
 */
export async function invalidateEcommerceUsageCache(accountId) {
  const { mutate } = await import('swr')
  if (accountId) {
    await mutate(['ecommerce-usage', accountId], undefined, { revalidate: true })
  }
}
